import type { Store } from 'redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevToolsLogOnlyInProduction } from '@redux-devtools/extension';
import type { RootState } from 'app/reducers/rootReducer.ts';
import { rootReducer } from 'app/reducers/rootReducer.ts';
import createSagaMiddleware from 'redux-saga';
import rootSaga from '../middleware/sagas/rootSaga.ts';
import { produce } from 'immer';

import AudioAction from 'app/actions/audio/audioAction.ts';
import { sideBarOpenSignal } from 'app/signals/sidebarStateSignal.ts';

export type AppDispatch = Store<RootState>['dispatch'];

export function configureStore(initialState?: RootState): [Store<RootState>] {
  // Redux DevTools extension configuration
  const devTools = composeWithDevToolsLogOnlyInProduction({
    // trace: true,
    // traceLimit: 25,
    actionSanitizer: (action) => {
      switch (action.type) {
        case AudioAction.Type.SET_AUDIO_STORE:
          return { ...action, payload: (action as any).payload && '[[AUDIO_BLOB]]' };
      }

      return action;
    },

    stateSanitizer: (state) => {
      const s = state as RootState;
      const updatedState: RootState = {
        ...s,
        audioReducer: produce(s.audioReducer, (r) => {
          if (r.voice) {
            r.voice = '[[AUDIO_BLOB]]';
          }
        })
      };

      return updatedState as any;
    }
  });

  const sagaMiddleware = createSagaMiddleware();

  // Apply middleware and Redux DevTools enhancement
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || devTools;
  const store = createStore(rootReducer, composeEnhancers(applyMiddleware(sagaMiddleware)));

  // TODO(signals-migration): remove this bridge when guiStateReducer is retired.
  sideBarOpenSignal.value = store.getState().guiStateReducer.sideMenuOpen;
  let previousSidebarOpen = sideBarOpenSignal.value;
  store.subscribe(() => {
    const nextSidebarOpen = store.getState().guiStateReducer.sideMenuOpen;
    if (nextSidebarOpen !== previousSidebarOpen) {
      previousSidebarOpen = nextSidebarOpen;
      sideBarOpenSignal.value = nextSidebarOpen;
    }
  });

  // HMR for reducers - removed hot module handling to fix TS error

  sagaMiddleware.run(rootSaga);

  return [store];
}
